/**
 * ====================================================================
 * FIKO TEKNIK RESIM MODU - THREE.JS TABANLI 2D TEKNİK RESİM
 * ====================================================================
 *
 * Ana 3D viewer içinde çalışan teknik resim modu.
 * Canvas2D değil, Three.js kullanır.
 * FikoFloatingToolbar ve FikoDialogService ile entegre.
 *
 * @author FikoYT CAD Team
 * @version 3.0.0
 */

import * as THREE from 'three';
import { Subject, Observable } from 'rxjs';
import { PaperSize, PaperOrientation, PAPER_DIMENSIONS, ViewType, STANDARD_VIEW_DIRECTIONS } from '../core/types';
import { FikoGrup } from '../../FikoGrup';

// TWEEN animasyon kütüphanesi (global)
declare var TWEEN: any;

/**
 * Teknik resim modu durumu
 */
export interface TeknikResimModuDurumu {
    aktif: boolean;
    kagitBoyutu: PaperSize;
    kagitYonu: PaperOrientation;
    olcek: number;
    gridGorunur: boolean;
    snapAktif: boolean;
}

/**
 * Teknik resim görünümü
 */
export interface TeknikResimGorunum {
    id: string;
    tip: ViewType;
    pozisyon: THREE.Vector2;
    olcek: number;
    nesneId: string;
    sinirKutusu: THREE.Box2;
}

/**
 * FikoTeknikResimModu - Ana 3D viewer içinde teknik resim modu
 */
export class FikoTeknikResimModu {
    // ====================================================================
    // REFERANSLAR
    // ====================================================================

    /** Ana 3D viewer (FikoThreeJsComponent) */
    private anaGosterim: any;

    /** Ana sahne (FikoSahne) */
    private sahne: any;

    /** Teknik resim grubu (tüm projeksiyonlar için container) */
    private teknikResimGrubu: FikoGrup | null = null;

    /** Görünümler */
    private gorunumler: Map<string, TeknikResimGorunum> = new Map();

    /** Projeksiyon grupları (her görünüm için) */
    private projeksiyonlar: Map<string, FikoGrup> = new Map();

    /** Görünüm sayacı (pozisyon hesaplamak için) */
    private gorunumSayaci: number = 0;

    // ====================================================================
    // DURUM
    // ====================================================================

    private _durum: TeknikResimModuDurumu = {
        aktif: false,
        kagitBoyutu: PaperSize.A3,
        kagitYonu: PaperOrientation.LANDSCAPE,
        olcek: 1,
        gridGorunur: true,
        snapAktif: true
    };

    // ====================================================================
    // EVENTS
    // ====================================================================

    private durumDegisti$ = new Subject<TeknikResimModuDurumu>();
    private gorunumEklendi$ = new Subject<TeknikResimGorunum>();
    private gorunumSilindi$ = new Subject<string>();

    // ====================================================================
    // CONSTRUCTOR
    // ====================================================================

    constructor(anaGosterim: any) {
        this.anaGosterim = anaGosterim;
        this.sahne = anaGosterim?.sahneler?.[0];
    }

    // ====================================================================
    // PUBLIC API
    // ====================================================================

    /**
     * Teknik resim modunu aç
     */
    ac(): void {
        if (this._durum.aktif) return;

        console.log('[FikoTeknikResimModu] Mod açılıyor...');

        // Teknik resim ana grubu oluştur - FikoGrup kullan (FikoKaynakca pattern)
        this.teknikResimGrubu = new FikoGrup(this.sahne, 'Teknik Resimler');

        // Ürün ağacında görünmesi için FikoArayuz ayarla (FikoEksen/FikoKaynakca pattern)
        this.teknikResimGrubu.userData['FikoArayuz'].agactaGorunur = true;
        this.teknikResimGrubu.userData['FikoArayuz'].listeGoster = true;
        this.teknikResimGrubu.userData['FikoArayuz'].simge = './simgeler/teknik-resim.svg';
        this.teknikResimGrubu.userData['tip'] = 'TeknikResimGrubu';

        // Ürün ağacına ekle - ekle() metodunu kullan (add değil!)
        const urunAgaci = this.sahne?.urunAgaci;
        if (urunAgaci && typeof urunAgaci.ekle === 'function') {
            urunAgaci.ekle(this.teknikResimGrubu);
            console.log('[FikoTeknikResimModu] Ürün ağacına eklendi (ekle metodu)');
        } else if (this.sahne && typeof this.sahne.ekle === 'function') {
            this.sahne.ekle(this.teknikResimGrubu);
            console.log('[FikoTeknikResimModu] Sahneye eklendi (ekle metodu)');
        } else if (urunAgaci) {
            // Fallback: add kullan
            urunAgaci.add(this.teknikResimGrubu);
            console.log('[FikoTeknikResimModu] Ürün ağacına eklendi (add fallback)');
        }

        // UI güncelleme tetikle
        this.anaGosterim?.FikoYT?.degisti?.emit();

        // Modu aktif et
        this._durum.aktif = true;
        this.gorunumSayaci = 0;
        this.durumDegisti$.next({ ...this._durum });

        // Render
        this.render();

        console.log('[FikoTeknikResimModu] Mod aktif');
    }

    /**
     * Teknik resim modunu kapat
     */
    kapat(): void {
        if (!this._durum.aktif) return;

        console.log('[FikoTeknikResimModu] Mod kapatılıyor...');

        // Teknik resim grubunu temizle ve ürün ağacından/sahneden kaldır
        if (this.teknikResimGrubu) {
            // Tüm child'ları temizle
            while (this.teknikResimGrubu.children.length > 0) {
                const child = this.teknikResimGrubu.children[0];
                this.teknikResimGrubu.remove(child);
            }

            // Parent'tan kaldır (urunAgaci veya sahne)
            if (this.teknikResimGrubu.parent) {
                this.teknikResimGrubu.parent.remove(this.teknikResimGrubu);
            }
            this.teknikResimGrubu = null;
        }

        this.projeksiyonlar.clear();
        this.gorunumler.clear();

        this._durum.aktif = false;
        this.durumDegisti$.next({ ...this._durum });

        // Render
        this.render();

        console.log('[FikoTeknikResimModu] Mod kapatıldı');
    }

    /**
     * Mod toggle
     */
    toggle(): void {
        if (this._durum.aktif) {
            this.kapat();
        } else {
            this.ac();
        }
    }

    /**
     * Görünüm ekle (Seçili nesne için)
     */
    gorunumEkle(tip: ViewType, pozisyon?: THREE.Vector2): TeknikResimGorunum | null {
        if (!this._durum.aktif) {
            console.warn('[FikoTeknikResimModu] Mod aktif değil - önce modu açın');
            this.bildirimGoster('Önce Teknik Resim modunu açın!', 'warning');
            return null;
        }

        // Seçili nesneyi al - önce aktifItem (ağaç seçimi), yoksa secililer (viewport seçimi)
        let seciliNesne: THREE.Object3D | null = this.anaGosterim?.aktifItem;

        // aktifItem yoksa, 3D viewport seçimlerinden al
        if (!seciliNesne) {
            const secililer = this.anaGosterim?.secililer;

            // secililer.liste bir Intersection[] veya Object3D[] olabilir
            if (secililer?.liste?.length > 0) {
                const ilk = secililer.liste[0];
                // Intersection objesi mi yoksa direkt Object3D mi kontrol et
                seciliNesne = ilk?.object || ilk;
            }
            // children'dan da bak
            else if (secililer?.children?.length > 0) {
                const ilk = secililer.children[0];
                seciliNesne = ilk?.object || ilk;
            }
        }

        // Mesh olup olmadığını kontrol et - Group ise içindeki ilk Mesh'i al
        if (seciliNesne && !(seciliNesne as THREE.Mesh).isMesh) {
            let ilkMesh: THREE.Mesh | null = null;
            seciliNesne.traverse((child: THREE.Object3D) => {
                if (!ilkMesh && (child as THREE.Mesh).isMesh) {
                    ilkMesh = child as THREE.Mesh;
                }
            });
            if (ilkMesh) {
                // Group'u kullan (tüm mesh'leri içerir)
                console.log('[FikoTeknikResimModu] Group seçildi, tüm mesh\'ler kullanılacak');
            }
        }

        if (!seciliNesne) {
            console.warn('[FikoTeknikResimModu] Seçili nesne yok - önce 3D sahnede bir nesne seçin');
            this.bildirimGoster('Önce 3D sahnede bir nesne seçin!', 'warning');
            return null;
        }

        console.log('[FikoTeknikResimModu] Seçili nesne bulundu:', seciliNesne.name || seciliNesne.uuid);

        // ID oluştur
        const id = `gorunum_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Nesnenin bounding box'ını al
        const box = new THREE.Box3().setFromObject(seciliNesne);
        const merkez = box.getCenter(new THREE.Vector3());
        const boyut = box.getSize(new THREE.Vector3());
        const maxBoyut = Math.max(boyut.x, boyut.y, boyut.z);

        // Projeksiyon oluştur
        const projeksiyonGrubu = this.projeksiyonOlustur(seciliNesne, tip, merkez, boyut);
        if (!projeksiyonGrubu) return null;

        // Pozisyon hesapla - her yeni görünüm yanyana dizilsin
        const offset = this.gorunumSayaci * (maxBoyut * 1.5);
        projeksiyonGrubu.position.set(
            merkez.x + offset,
            merkez.y - maxBoyut * 2, // Orijinal nesnenin altına
            merkez.z
        );

        this.gorunumSayaci++;

        // Teknik resim grubuna ekle - ekle() metodunu kullan (add değil!)
        if (this.teknikResimGrubu) {
            if (typeof this.teknikResimGrubu.ekle === 'function') {
                this.teknikResimGrubu.ekle(projeksiyonGrubu);
            } else {
                this.teknikResimGrubu.add(projeksiyonGrubu);
            }
            console.log('[FikoTeknikResimModu] Projeksiyon sahneye eklendi. Pozisyon:', projeksiyonGrubu.position);
        }

        // UI güncelleme tetikle (ürün ağacı için)
        this.anaGosterim?.FikoYT?.degisti?.emit();

        // Sınır kutusunu hesapla
        const newBox = new THREE.Box3().setFromObject(projeksiyonGrubu);
        const sinirKutusu = new THREE.Box2(
            new THREE.Vector2(newBox.min.x, newBox.min.y),
            new THREE.Vector2(newBox.max.x, newBox.max.y)
        );

        // Görünüm verisi
        const poz = pozisyon || new THREE.Vector2(projeksiyonGrubu.position.x, projeksiyonGrubu.position.y);
        const gorunum: TeknikResimGorunum = {
            id,
            tip,
            pozisyon: poz,
            olcek: this._durum.olcek,
            nesneId: seciliNesne.uuid,
            sinirKutusu
        };

        // Kaydet
        this.gorunumler.set(id, gorunum);
        this.projeksiyonlar.set(id, projeksiyonGrubu);

        // Event
        this.gorunumEklendi$.next(gorunum);

        // Render
        this.render();

        // Kamerayı projeksiyona odakla
        this.kamerayiProjeksiyonaOdakla(projeksiyonGrubu);

        // Kullanıcıya bildirim göster
        const tipAdlari: Record<string, string> = {
            'FRONT': 'Ön', 'BACK': 'Arka', 'TOP': 'Üst',
            'BOTTOM': 'Alt', 'LEFT': 'Sol', 'RIGHT': 'Sağ', 'ISOMETRIC': 'İzometrik'
        };
        this.bildirimGoster(`${tipAdlari[tip] || tip} görünüm eklendi!`, 'success');

        console.log('[FikoTeknikResimModu] Görünüm başarıyla eklendi:', tip);
        return gorunum;
    }

    /**
     * Görünüm sil
     */
    gorunumSil(id: string): void {
        const projGrup = this.projeksiyonlar.get(id);
        if (projGrup && this.teknikResimGrubu) {
            this.teknikResimGrubu.remove(projGrup);
        }

        this.gorunumler.delete(id);
        this.projeksiyonlar.delete(id);
        this.gorunumSilindi$.next(id);

        this.render();
    }

    /**
     * Kağıt boyutunu değiştir
     */
    kagitBoyutuAyarla(boyut: PaperSize, yon: PaperOrientation): void {
        this._durum.kagitBoyutu = boyut;
        this._durum.kagitYonu = yon;
        this.durumDegisti$.next({ ...this._durum });
    }

    /**
     * Grid görünürlüğünü değiştir
     */
    gridToggle(): void {
        this._durum.gridGorunur = !this._durum.gridGorunur;
        this.durumDegisti$.next({ ...this._durum });
        this.render();
    }

    /**
     * Snap durumunu değiştir
     */
    snapToggle(): void {
        this._durum.snapAktif = !this._durum.snapAktif;
        this.durumDegisti$.next({ ...this._durum });
    }

    // ====================================================================
    // GETTERS
    // ====================================================================

    get aktifMi(): boolean {
        return this._durum.aktif;
    }

    get durum(): TeknikResimModuDurumu {
        return { ...this._durum };
    }

    get durumDegisti(): Observable<TeknikResimModuDurumu> {
        return this.durumDegisti$.asObservable();
    }

    get gorunumEklendi(): Observable<TeknikResimGorunum> {
        return this.gorunumEklendi$.asObservable();
    }

    get gorunumSilindi(): Observable<string> {
        return this.gorunumSilindi$.asObservable();
    }

    tumGorunumler(): TeknikResimGorunum[] {
        return Array.from(this.gorunumler.values());
    }

    // ====================================================================
    // PRIVATE METHODS
    // ====================================================================

    /**
     * 3D nesneden 2D projeksiyon oluştur
     */
    private projeksiyonOlustur(
        nesne: THREE.Object3D,
        tip: ViewType,
        merkez: THREE.Vector3,
        boyut: THREE.Vector3
    ): FikoGrup | null {
        // Tip adları
        const tipAdlari: Record<string, string> = {
            'FRONT': 'Ön', 'BACK': 'Arka', 'TOP': 'Üst',
            'BOTTOM': 'Alt', 'LEFT': 'Sol', 'RIGHT': 'Sağ', 'ISOMETRIC': 'İzo'
        };

        // FikoGrup kullan - FikoEksen/FikoKaynakca pattern
        const grup = new FikoGrup(this.sahne, `${tipAdlari[tip] || tip} Görünüm`);

        // Ürün ağacında görünmesi için FikoArayuz ayarla
        grup.userData['FikoArayuz'].agactaGorunur = true;
        grup.userData['FikoArayuz'].listeGoster = false;
        grup.userData['FikoArayuz'].simge = './simgeler/teknik-resim.webp';
        grup.userData['tip'] = 'TeknikResimGorunum';
        grup.userData['gorunumTipi'] = tip;

        // Görünüm yönünü al
        const yon = STANDARD_VIEW_DIRECTIONS[tip];
        if (!yon) {
            console.warn('[FikoTeknikResimModu] Bilinmeyen görünüm tipi:', tip);
            return null;
        }

        console.log('[FikoTeknikResimModu] Projeksiyon oluşturuluyor. Tip:', tip, 'Yön:', yon);

        // CATIA gerçek HLR: B-Rep varsa analitik görünür(sürekli)/gizli(kesikli) çizgiler; yoksa mesh EdgesGeometry
        let kenarSayisi = 0;
        const brepKenar = this.brepHLRKenarlari(yon, merkez);
        if (brepKenar && brepKenar.children.length > 0) {
            grup.add(brepKenar);
            kenarSayisi = brepKenar.children.length;
        } else {
            nesne.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    if (mesh.geometry) {
                        const kenarlar = this.kenarCikar(mesh, yon, merkez);
                        if (kenarlar.children.length > 0) {
                            grup.add(kenarlar);
                            kenarSayisi += kenarlar.children.length;
                        }
                    }
                }
            });
        }

        console.log('[FikoTeknikResimModu] Toplam kenar sayısı:', kenarSayisi);

        // Etiket ekle
        const etiket = this.etiketOlustur(tip, boyut);
        grup.add(etiket);

        // Çerçeve ekle
        const cerceve = this.cercereOlustur(boyut);
        grup.add(cerceve);

        return grup;
    }

    /**
     * CATIA gerçek HLR: B-Rep analitik görünür/gizli çizgileri (kenarCikar ile AYNI projeksiyon eksenleri+merkez → koordinat uyumlu).
     * Görünür = sürekli siyah, gizli = kesikli. B-Rep yoksa null → mesh kenar yoluna düşülür.
     */
    private brepHLRKenarlari(bakisYonu: THREE.Vector3, merkez: THREE.Vector3): THREE.Group | null {
        const m = this.sahne?.anaGosterim?.modellemeAl?.();
        if (!m?.brepVarMi?.() || !m.hlrSegmentler3D) return null;
        let r: any; try { r = m.hlrSegmentler3D({ x: bakisYonu.x, y: bakisYonu.y, z: bakisYonu.z }); } catch { return null; }
        if (!r || (!r.gorunur?.length && !r.gizli?.length)) return null;
        // kenarCikar ile AYNI eksenler
        const up = new THREE.Vector3(0, 1, 0);
        if (Math.abs(bakisYonu.dot(up)) > 0.99) up.set(0, 0, 1);
        const right = new THREE.Vector3().crossVectors(up, bakisYonu).normalize();
        const realUp = new THREE.Vector3().crossVectors(bakisYonu, right).normalize();
        const g = new THREE.Group();
        const _p = new THREE.Vector3();
        const ciz = (polys: { x: number; y: number; z: number }[][], kesikli: boolean) => {
            for (const pl of polys || []) {
                if (!pl || pl.length < 2) continue;
                const pos = new Float32Array(pl.length * 3);
                for (let i = 0; i < pl.length; i++) {
                    _p.set(pl[i].x - merkez.x, pl[i].y - merkez.y, pl[i].z - merkez.z);
                    pos[i * 3] = _p.dot(right); pos[i * 3 + 1] = _p.dot(realUp); pos[i * 3 + 2] = 0;
                }
                const geo = new THREE.BufferGeometry();
                geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
                const mat = kesikli
                    ? new THREE.LineDashedMaterial({ color: 0x000000, dashSize: 2, gapSize: 1.2, depthTest: false })
                    : new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 2, depthTest: false });
                const line = new THREE.Line(geo, mat);   // polyline (LineSegments değil — kenar sürekli)
                if (kesikli) line.computeLineDistances();
                line.renderOrder = 100; line.frustumCulled = false;
                g.add(line);
            }
        };
        ciz(r.gorunur, false);
        ciz(r.gizli, true);
        return g;
    }

    /**
     * Mesh'ten kenarları çıkar ve 2D'ye projeksiyonla
     */
    private kenarCikar(mesh: THREE.Mesh, bakisYonu: THREE.Vector3, merkez: THREE.Vector3): THREE.Group {
        const kenarlar = new THREE.Group();

        try {
            // EdgesGeometry ile kenarları al
            const edges = new THREE.EdgesGeometry(mesh.geometry, 30); // 30 derece eşik
            const positions = edges.getAttribute('position');

            if (!positions || positions.count === 0) {
                return kenarlar;
            }

            // Bakış yönüne göre projeksiyon eksenleri belirle
            const up = new THREE.Vector3(0, 1, 0);
            if (Math.abs(bakisYonu.dot(up)) > 0.99) {
                up.set(0, 0, 1);
            }
            const right = new THREE.Vector3().crossVectors(up, bakisYonu).normalize();
            const realUp = new THREE.Vector3().crossVectors(bakisYonu, right).normalize();

            // Projeksiyon
            const projectedPositions = new Float32Array(positions.count * 3);

            for (let i = 0; i < positions.count; i++) {
                // World koordinatlarını al
                const localPos = new THREE.Vector3(
                    positions.getX(i),
                    positions.getY(i),
                    positions.getZ(i)
                );

                // World transform uygula
                localPos.applyMatrix4(mesh.matrixWorld);

                // Merkeze göre ofset
                const x = localPos.x - merkez.x;
                const y = localPos.y - merkez.y;
                const z = localPos.z - merkez.z;

                const point = new THREE.Vector3(x, y, z);

                // 2D koordinatlara projeksiyonla
                projectedPositions[i * 3] = point.dot(right);
                projectedPositions[i * 3 + 1] = point.dot(realUp);
                projectedPositions[i * 3 + 2] = 0;
            }

            const projectedGeometry = new THREE.BufferGeometry();
            projectedGeometry.setAttribute('position', new THREE.BufferAttribute(projectedPositions, 3));

            // Görünür material ayarları
            const edgeMaterial = new THREE.LineBasicMaterial({
                color: 0x000000,
                linewidth: 2,
                depthTest: true,
                depthWrite: true,
                transparent: false
            });

            const projectedLines = new THREE.LineSegments(projectedGeometry, edgeMaterial);
            projectedLines.renderOrder = 100; // Üstte render et
            projectedLines.frustumCulled = false; // Her zaman çiz
            kenarlar.add(projectedLines);

        } catch (e) {
            console.error('[FikoTeknikResimModu] Kenar çıkarma hatası:', e);
        }

        return kenarlar;
    }

    /**
     * Görünüm etiketi oluştur (ör: "ÖN GÖRÜNÜM")
     */
    private etiketOlustur(tip: ViewType, boyut: THREE.Vector3): THREE.Group {
        const etiketGrup = new THREE.Group();

        // Tip adını al
        const tipAdlari: Record<string, string> = {
            'FRONT': 'ÖN',
            'BACK': 'ARKA',
            'TOP': 'ÜST',
            'BOTTOM': 'ALT',
            'LEFT': 'SOL',
            'RIGHT': 'SAĞ',
            'ISOMETRIC': 'İZO'
        };

        const ad = tipAdlari[tip] || tip;
        const maxBoyut = Math.max(boyut.x, boyut.y, boyut.z);

        // Basit etiket çizgisi
        const geometry = new THREE.BufferGeometry();
        const vertices = new Float32Array([
            -maxBoyut / 2, -maxBoyut / 2 - 10, 0,
            maxBoyut / 2, -maxBoyut / 2 - 10, 0
        ]);
        geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

        const lineMat = new THREE.LineBasicMaterial({
            color: 0x0066CC,
            linewidth: 2,
            depthTest: true
        });
        const line = new THREE.Line(geometry, lineMat);
        line.renderOrder = 101;
        line.frustumCulled = false;
        etiketGrup.add(line);

        // Sprite ile metin
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 128;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.fillStyle = '#0066CC';
            ctx.font = 'bold 48px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(ad + ' GÖRÜNÜM', 256, 64);
        }

        const texture = new THREE.CanvasTexture(canvas);
        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            depthTest: false,
            depthWrite: false,
            transparent: true
        });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.scale.set(maxBoyut * 1.5, maxBoyut / 3, 1);
        sprite.position.set(0, -maxBoyut / 2 - maxBoyut * 0.3, 0.1);
        sprite.renderOrder = 102;
        etiketGrup.add(sprite);

        return etiketGrup;
    }

    /**
     * Görünüm çerçevesi oluştur
     */
    private cercereOlustur(boyut: THREE.Vector3): THREE.Group {
        const cerceve = new THREE.Group();

        const maxBoyut = Math.max(boyut.x, boyut.y, boyut.z);
        const padding = maxBoyut * 0.1;
        const w = maxBoyut / 2 + padding;
        const h = maxBoyut / 2 + padding;

        const geometry = new THREE.BufferGeometry();
        const vertices = new Float32Array([
            -w, -h, 0,
            w, -h, 0,
            w, h, 0,
            -w, h, 0,
            -w, -h, 0
        ]);
        geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

        const lineMat = new THREE.LineBasicMaterial({
            color: 0x666666,
            linewidth: 2,
            depthTest: true
        });
        const line = new THREE.Line(geometry, lineMat);
        line.renderOrder = 99;
        line.frustumCulled = false;
        cerceve.add(line);

        // Arka plan (beyaz, hafif şeffaf)
        const bgGeometry = new THREE.PlaneGeometry(w * 2, h * 2);
        const bgMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.9,
            side: THREE.DoubleSide,
            depthTest: true,
            depthWrite: false
        });
        const bgPlane = new THREE.Mesh(bgGeometry, bgMaterial);
        bgPlane.position.set(0, 0, -0.1);
        bgPlane.renderOrder = 98;
        cerceve.add(bgPlane);

        return cerceve;
    }

    /**
     * Render
     */
    private render(): void {
        // ciz fonksiyonunu çağır
        if (this.anaGosterim?.ciz) {
            this.anaGosterim.ciz();
        }
        // Alternatif olarak animationLoop'u tetikle
        if (this.anaGosterim?.animasyonBaslat) {
            this.anaGosterim.animasyonBaslat();
        }
    }

    /**
     * Kamerayı projeksiyona odakla - FikoKonumlar.gitVeri() kullanır
     */
    private kamerayiProjeksiyonaOdakla(projeksiyonGrubu: FikoGrup): void {
        try {
            const box = new THREE.Box3().setFromObject(projeksiyonGrubu);
            const merkez = box.getCenter(new THREE.Vector3());
            const boyut = box.getSize(new THREE.Vector3());
            const maxBoyut = Math.max(boyut.x, boyut.y, boyut.z);

            // Kamera ve canvas bilgilerini al
            const aktifKamera = this.anaGosterim?.aktifKamera || this.sahne?.camera;
            const canvas = this.anaGosterim?.canvas?.nativeElement;

            if (!aktifKamera || !canvas) {
                console.warn('[FikoTeknikResimModu] Kamera veya canvas bulunamadı');
                return;
            }

            // Aspect ratio ve zoom hesapla
            const aspect = canvas.clientWidth / canvas.clientHeight;
            const radius = maxBoyut / 2 * 1.5;
            const yakinlik = (aspect > 1.0)
                ? canvas.clientHeight / (radius * 2)
                : canvas.clientWidth / (radius * 2);

            // 2D projeksiyon için ön görünüm (Z ekseninden bak)
            const kameraPoz = new THREE.Vector3(
                merkez.x,
                merkez.y,
                merkez.z + maxBoyut * 2
            );

            // Konum verisi oluştur (FikoKonumlar formatında)
            const konumVerisi = {
                position: kameraPoz.toArray(),
                quaternion: [0, 0, 0, 1], // Ön bakış
                hedef: merkez.toArray(),
                ust: [0, 1, 0],
                yakinlik: yakinlik,
                oran: aspect
            };

            // FikoKonumlar.gitVeri() kullan - animasyonlu geçiş
            const konumlar = this.sahne?.kaynakca?.konumlar;
            if (konumlar && typeof konumlar.gitVeri === 'function') {
                konumlar.gitVeri(konumVerisi, 500);
                console.log('[FikoTeknikResimModu] Kamera projeksiyona odaklandı (gitVeri):', merkez);
            } else {
                // Fallback: Manuel animasyon
                this.manuelKameraAnimasyonu(aktifKamera, kameraPoz, merkez, yakinlik);
            }
        } catch (e) {
            console.warn('[FikoTeknikResimModu] Kamera odaklama hatası:', e);
        }
    }

    /**
     * Manuel kamera animasyonu (FikoKonumlar yoksa)
     */
    private manuelKameraAnimasyonu(
        kamera: THREE.Camera,
        hedefPoz: THREE.Vector3,
        hedefHedef: THREE.Vector3,
        yakinlik: number
    ): void {
        const controls = this.anaGosterim?.aktifKontrol || this.sahne?.controls;

        if (typeof TWEEN !== 'undefined') {
            const baslangicPoz = kamera.position.clone();
            const baslangicHedef = controls?.target?.clone() || new THREE.Vector3();

            // Sürekli çizimi başlat
            this.anaGosterim?.surekliCiz?.();

            new TWEEN.Tween({ t: 0 })
                .to({ t: 1 }, 500)
                .easing(TWEEN.Easing.Quadratic.Out)
                .onUpdate((obj: any) => {
                    kamera.position.lerpVectors(baslangicPoz, hedefPoz, obj.t);
                    if (controls?.target) {
                        controls.target.lerpVectors(baslangicHedef, hedefHedef, obj.t);
                    }
                    controls?.update?.();

                    // Zoom animasyonu
                    if ((kamera as any).zoom !== undefined) {
                        const baslangicZoom = (kamera as any).zoom || 1;
                        (kamera as any).zoom = baslangicZoom + (yakinlik - baslangicZoom) * obj.t;
                        (kamera as any).updateProjectionMatrix?.();
                    }
                })
                .onComplete(() => {
                    this.anaGosterim?.surekliCizIptal?.();
                })
                .start();
        } else {
            // TWEEN yoksa direkt geçiş
            kamera.position.copy(hedefPoz);
            if (controls?.target) {
                controls.target.copy(hedefHedef);
            }
            if ((kamera as any).zoom !== undefined) {
                (kamera as any).zoom = yakinlik;
                (kamera as any).updateProjectionMatrix?.();
            }
            controls?.update?.();
            this.render();
        }
    }

    /**
     * Kullanıcıya bildirim göster
     */
    private bildirimGoster(mesaj: string, tip: 'success' | 'warning' | 'error' | 'info' = 'info'): void {
        // FikoDialogService veya NzMessageService varsa kullan
        try {
            // Angular injector üzerinden NzMessageService'e erişim dene
            const messageService = this.anaGosterim?.injector?.get?.('NzMessageService') ||
                                   (window as any)['__nzMessage'];

            if (messageService?.create) {
                messageService.create(tip, mesaj);
                return;
            }

            // Fallback: Console + DOM notification
            const renkler: Record<string, string> = {
                success: '#52c41a',
                warning: '#faad14',
                error: '#ff4d4f',
                info: '#1890ff'
            };

            // Basit DOM notification
            const notif = document.createElement('div');
            notif.style.cssText = `
                position: fixed;
                top: 80px;
                left: 50%;
                transform: translateX(-50%);
                background: ${renkler[tip]};
                color: white;
                padding: 12px 24px;
                border-radius: 8px;
                font-size: 14px;
                font-weight: 500;
                z-index: 10000;
                box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                animation: fadeInOut 2s ease-in-out;
            `;
            notif.textContent = mesaj;

            // Animation style ekle
            if (!document.getElementById('teknikResimNotifStyle')) {
                const style = document.createElement('style');
                style.id = 'teknikResimNotifStyle';
                style.textContent = `
                    @keyframes fadeInOut {
                        0% { opacity: 0; transform: translateX(-50%) translateY(-20px); }
                        15% { opacity: 1; transform: translateX(-50%) translateY(0); }
                        85% { opacity: 1; transform: translateX(-50%) translateY(0); }
                        100% { opacity: 0; transform: translateX(-50%) translateY(-20px); }
                    }
                `;
                document.head.appendChild(style);
            }

            document.body.appendChild(notif);
            setTimeout(() => notif.remove(), 2000);

        } catch (e) {
            // En kötü durumda sadece console
            console.log(`[TeknikResim ${tip.toUpperCase()}] ${mesaj}`);
        }
    }

    /**
     * Temizlik
     */
    dispose(): void {
        this.kapat();
        this.durumDegisti$.complete();
        this.gorunumEklendi$.complete();
        this.gorunumSilindi$.complete();
    }
}
